import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Plus, PencilSimple, Trash, Sparkle } from '@phosphor-icons/react'
import { toast } from 'sonner'
import type { GameAbility } from '@/types'
import { EditAbilityDialog } from './EditAbilityDialog'

interface AbilitiesTabProps {
  tableId: string
  abilities: GameAbility[]
  isGameMaster: boolean
  onAbilitiesChange: () => void
  onSaveAbility: (abilityData: Omit<GameAbility, 'id'> & { id?: string }) => Promise<void>
  onDeleteAbility: (abilityId: string) => Promise<void>
}

export function AbilitiesTab({ abilities, isGameMaster, onAbilitiesChange, onSaveAbility, onDeleteAbility }: AbilitiesTabProps) {
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [editingAbility, setEditingAbility] = useState<GameAbility | null>(null)
  const [isDeleting, setIsDeleting] = useState<string | null>(null)

  const handleCreateAbility = () => {
    setEditingAbility(null)
    setShowEditDialog(true)
  }

  const handleEditAbility = (ability: GameAbility) => {
    setEditingAbility(ability)
    setShowEditDialog(true)
  }

  const handleDeleteAbility = async (abilityId: string, abilityName: string) => {
    if (!confirm(`Tem certeza que deseja excluir a habilidade "${abilityName}"?\n\nEsta ação não pode ser desfeita.`)) {
      return
    }

    setIsDeleting(abilityId)
    try {
      await onDeleteAbility(abilityId)
      toast.success(`Habilidade "${abilityName}" excluída com sucesso`)
      onAbilitiesChange()
    } catch (error: any) {
      console.error('Erro ao excluir habilidade:', error)
      const errorMessage = error.response?.data?.detail || error.message || 'Erro ao excluir habilidade'
      toast.error(errorMessage)
    } finally {
      setIsDeleting(null)
    }
  }

  const handleSaveAbility = async (abilityData: Omit<GameAbility, 'id'> & { id?: string }) => {
    await onSaveAbility(abilityData)
    toast.success(editingAbility ? 'Habilidade atualizada com sucesso' : 'Habilidade criada com sucesso')
    onAbilitiesChange()
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">Habilidades da Campanha</h3>
          <p className="text-sm text-muted-foreground">
            Catálogo de habilidades que o mestre pode conceder aos personagens
          </p>
        </div>
        {isGameMaster && (
          <Button onClick={handleCreateAbility}>
            <Plus size={16} className="mr-2" />
            Nova Habilidade
          </Button>
        )}
      </div>

      {abilities.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <div className="rounded-full bg-muted p-4 mb-4">
              <Sparkle size={32} className="text-muted-foreground" />
            </div>
            <h4 className="text-lg font-medium mb-2">Nenhuma habilidade configurada</h4>
            <p className="text-muted-foreground mb-4 max-w-md">
              {isGameMaster
                ? 'Crie habilidades para sua campanha. Você poderá concedê-las aos personagens durante o jogo.'
                : 'O mestre ainda não configurou habilidades para esta campanha.'}
            </p>
            {isGameMaster && (
              <Button onClick={handleCreateAbility}>
                <Plus size={16} className="mr-2" />
                Criar Primeira Habilidade
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {abilities.map((ability) => (
            <Card key={ability.id} className="group hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="text-3xl">{ability.icon}</div>
                    <CardTitle className="text-lg">{ability.name}</CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <CardDescription className="line-clamp-3">
                  {ability.description}
                </CardDescription>

                {isGameMaster && (
                  <div className="flex gap-2 pt-2 transition-opacity md:opacity-0 md:group-hover:opacity-100">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={() => handleEditAbility(ability)}
                    >
                      <PencilSimple size={14} className="mr-1" />
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteAbility(ability.id, ability.name)}
                      disabled={isDeleting === ability.id}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash size={14} />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <EditAbilityDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        abilityData={editingAbility}
        onSave={handleSaveAbility}
      />
    </>
  )
}
